import CurrencyInput from "../input/CurrencyInput";
import NumberInput from "../input/NumberInput";
import type { ItemRow } from "./DocumentItemTable";



interface DocumentSummaryProps {
  items: ItemRow[];
  discount: number;
  ppn: number;
  onDiscountChange: (value: number) => void;
  onPpnChange: (value: number) => void;
}

const formatIDR = (n: number) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(n || 0);

export default function DocumentSummary({
  items,
  discount,
  ppn,
  onDiscountChange,
  onPpnChange,
}: DocumentSummaryProps) {
  const subtotal = items.reduce((sum, item) => sum + item.qty * item.price, 0);
  const discountAmount = subtotal * (discount / 100);
  const totalAfterDiscount = subtotal - discountAmount;
  const ppnAmount = totalAfterDiscount * (ppn / 100);
  const grandTotal = totalAfterDiscount + ppnAmount;

  return (
    <div className="flex justify-end">
      <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between py-1.5 text-sm">
          <span className="text-gray-500">Total price</span>
          <div className="w-32">
            <CurrencyInput value={subtotal} readOnly aria-label="Total price" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
          <span className="text-gray-500">Discount</span>
          <div className="flex items-center gap-2">
            <div className="w-20">
              <NumberInput
                value={discount}
                onChange={onDiscountChange}
                max={100}
                suffix="%"
                aria-label="Discount percentage"
              />
            </div>
            <span className="w-28 text-right tabular-nums text-gray-500">
              −{formatIDR(discountAmount)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
          <span className="text-gray-500">PPN</span>
          <div className="flex items-center gap-2">
            <div className="w-20">
              <NumberInput
                value={ppn}
                onChange={onPpnChange}
                max={100}
                suffix="%"
                aria-label="PPN percentage"
              />
            </div>
            <span className="w-28 text-right tabular-nums text-gray-500">
              +{formatIDR(ppnAmount)}
            </span>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-3">
          <span className="text-sm font-semibold text-gray-700">Grand total</span>
          <span className="text-lg font-semibold tabular-nums text-gray-900">
            {formatIDR(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}
